import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { tokens } from '../ui/tokens';
import {
  initGame,
  startInput,
  tapTile,
  nextRound,
  type GameState,
  type Tile,
} from '../domain/game-rules.js';
import styles from './JumpingStickmanGame.module.css';

/**
 * 火柴人跳格子 — 小小互動，呼應店內 iDancing 地板格子遊戲。
 *
 * 訂位成功後在確認頁 mount 一次；不擋 booking 流程。
 * 規則 / 狀態機在 `../domain/game-rules.ts`（純 fn），本檔只負責
 * 渲染 + 輸入事件 + 動畫 timing。這樣規則 100% 可單測，渲染用
 * 純 SVG + CSS keyframe，無 canvas / Phaser / framer-motion。
 *
 * A11y:
 *   - 每格 <button> with aria-label
 *   - Keyboard: Tab 切格，Enter/Space 觸發 tap
 *   - `prefers-reduced-motion`: reduce → CSS 停用 animation / transition
 */

interface Props {
  readonly partySize: number;
  /** Callback when user closes / completes the game. Optional. */
  readonly onDone?: () => void;
}

const SHOW_INTERVAL_MS = 600; // time between target flashes during "show"
const SHOW_LINGER_MS = 400; // how long the last flash stays lit
const AFTER_SUCCESS_DELAY_MS = 900;
const AFTER_FAIL_DELAY_MS = 900;

export function JumpingStickmanGame({ partySize, onDone }: Props): JSX.Element {
  const [state, setState] = useState<GameState>(() => initGame({ partySize }));
  const [flashingIdx, setFlashingIdx] = useState<number>(-1); // which target-seq tile is currently lit
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // "show" phase: flash the target sequence tile-by-tile then transition to input
  useEffect(() => {
    if (state.phase !== 'show') return;
    let idx = 0;
    setFlashingIdx(0);
    const tick = (): void => {
      idx += 1;
      if (idx < state.targetSequence.length) {
        setFlashingIdx(idx);
        timerRef.current = setTimeout(tick, SHOW_INTERVAL_MS);
      } else {
        timerRef.current = setTimeout(() => {
          setFlashingIdx(-1);
          setState((s) => startInput(s));
        }, SHOW_LINGER_MS);
      }
    };
    timerRef.current = setTimeout(tick, SHOW_INTERVAL_MS);
    return cancelTimer;
  }, [state.phase, state.targetSequence, state.roundIndex, cancelTimer]);

  // after success or fail, auto-advance after a short linger
  useEffect(() => {
    if (state.phase !== 'success' && state.phase !== 'fail') return;
    const delay = state.phase === 'success' ? AFTER_SUCCESS_DELAY_MS : AFTER_FAIL_DELAY_MS;
    timerRef.current = setTimeout(() => {
      setState((s) => nextRound(s));
    }, delay);
    return cancelTimer;
  }, [state.phase, cancelTimer]);

  // call onDone when game completes
  useEffect(() => {
    if (state.phase === 'done' && onDone) onDone();
  }, [state.phase, onDone]);

  const handleTileClick = useCallback((tile: Tile) => {
    setState((s) => tapTile(s, tile));
  }, []);

  const gridClass = state.gridSize === 4 ? styles['grid4'] : styles['grid3'];

  const phaseHint = useMemo((): string => {
    switch (state.phase) {
      case 'show':
        return '🔥 看好順序…';
      case 'input':
        return '👉 你的回合！按照順序點回格子';
      case 'success':
        return '🎉 對了！';
      case 'fail':
        return '❌ 順序不對，下一輪再試';
      case 'done':
        return '玩完啦 — 店裡的真實版等你！';
      default:
        return '';
    }
  }, [state.phase]);

  if (state.phase === 'done') {
    return (
      <div className={styles['wrap']}>
        <p className={styles['doneMsg']}>
          得分 {state.score} / {state.roundsRequired}
          <br />
          <small>店裡面有真正的地板可以跳！</small>
        </p>
        <button
          type="button"
          className={styles['resetBtn']}
          onClick={() => setState(initGame({ partySize }))}
        >
          再玩一次
        </button>
      </div>
    );
  }

  return (
    <div className={styles['wrap']} role="region" aria-label="跳格子小遊戲">
      <p className={styles['title']}>🕹️ 跳格子試玩</p>
      <p className={styles['subtitle']}>
        回合 {state.roundIndex + 1} / {state.roundsRequired} · 得分 {state.score}
      </p>

      <div className={`${styles['grid']} ${gridClass}`} aria-live="polite">
        {Array.from({ length: state.gridSize }, (_, row) =>
          Array.from({ length: state.gridSize }, (_, col) => {
            const tile: Tile = { row, col };
            const isTarget =
              state.phase === 'show' &&
              flashingIdx >= 0 &&
              state.targetSequence[flashingIdx] &&
              state.targetSequence[flashingIdx]!.row === row &&
              state.targetSequence[flashingIdx]!.col === col;
            const isTapped = state.inputSequence.some(
              (t) => t.row === row && t.col === col,
            );
            const isFailHighlight =
              state.phase === 'fail' && flashingIdx === -1 && isTapped;
            const classes = [styles['tile']];
            if (isTarget) classes.push(styles['lit']!);
            else if (state.phase === 'success' && isTapped) classes.push(styles['correct']!);
            else if (isFailHighlight) classes.push(styles['wrong']!);
            else if (isTapped) classes.push(styles['tapped']!);

            return (
              <button
                key={`${row}-${col}`}
                type="button"
                className={classes.join(' ')}
                aria-label={`格 (${row + 1}, ${col + 1})`}
                disabled={state.phase !== 'input'}
                onClick={() => handleTileClick(tile)}
              />
            );
          }),
        )}
        {/* stickman overlay — positioned absolutely over the tile */}
        <Stickman stickman={state.stickman} gridSize={state.gridSize} />
      </div>

      <p className={styles['phaseHint']} aria-live="polite">{phaseHint}</p>
    </div>
  );
}

/**
 * 火柴人 SVG — 8 條 line，零 asset。絕對定位在對應格子上方。
 */
function Stickman({ stickman, gridSize }: { stickman: Tile; gridSize: number }): JSX.Element {
  // Compute left/top as percentage of grid (simple approximation — tile
  // spacing is 6px gap + padding 8px, but percentage works fine for 3×3/4×4).
  const cellPct = 100 / gridSize;
  const left = stickman.col * cellPct + cellPct / 2;
  const top = stickman.row * cellPct + cellPct / 2;
  return (
    <svg
      className={`${styles['stickman']} ${styles['stickmanHop']}`}
      style={{ left: `calc(${left}% - 16px)`, top: `calc(${top}% - 32px)` }}
      viewBox="0 0 32 40"
      aria-hidden="true"
    >
      {/* head */}
      <circle cx="16" cy="6" r="4" fill="none" stroke={tokens.game.stickmanStroke} strokeWidth="2" />
      {/* body */}
      <line x1="16" y1="10" x2="16" y2="24" stroke={tokens.game.stickmanStroke} strokeWidth="2" />
      {/* arms */}
      <line x1="16" y1="14" x2="8" y2="20" stroke={tokens.game.stickmanStroke} strokeWidth="2" />
      <line x1="16" y1="14" x2="24" y2="20" stroke={tokens.game.stickmanStroke} strokeWidth="2" />
      {/* legs */}
      <line x1="16" y1="24" x2="10" y2="36" stroke={tokens.game.stickmanStroke} strokeWidth="2" />
      <line x1="16" y1="24" x2="22" y2="36" stroke={tokens.game.stickmanStroke} strokeWidth="2" />
    </svg>
  );
}
